import React, { useState } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { useDeviceDetection } from '../hooks/useDeviceDetection';
import { useAuth } from '../hooks/useAuth';
import PersistentHeader from '../components/PersistentHeader';
import '../pages/Dashboard/Dashboard.css';

/**
 * ResponsiveLayout - Clean, simple layout that works on both mobile and desktop
 * Uses a single layout structure that adapts with CSS media queries
 */
const ResponsiveLayout: React.FC = () => {
  const { isMobile, isTablet } = useDeviceDetection();
  const { user } = useAuth();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Mobile/tablet should always have collapsed sidebar with no toggle
  const isMobileOrTablet = isMobile || isTablet;
  
  // Check if user is admin
  const isAdmin = user?.role === 'admin';
  
  // Hide navigation AND header on game pages for immersive experience
  const isGamePage = location.pathname.includes('/game') || 
                     location.pathname.includes('/lobby');

  // Admin panel is a standalone full-width page (has its own header + "Back to Game")
  const isAdminPage = location.pathname.includes('/admin');

  if (isGamePage || isAdminPage) {
    // Game pages get full screen without navigation or header
    return (
      <div className="game-layout">
        <Outlet />
      </div>
    );
  }

  return (
    <>
      {/* Persistent Header - Shows on all non-game pages */}
      <PersistentHeader />
      
      <div 
        className="dashboard-container" 
        style={{ 
          paddingTop: window.innerWidth >= 1024 ? '70px' : window.innerWidth >= 768 ? '60px' : '60px'
        }}
      >
        {/* Sidebar Navigation */}
        <div className={`sidebar ${(sidebarCollapsed || isMobileOrTablet) ? 'collapsed' : ''}`}>
        {/* Sidebar Toggle Button - Only show on desktop */}
        {!isMobileOrTablet && (
          <button 
            className="sidebar-toggle-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>
        )}

        {/* Sidebar Title */}
        <div className="sidebar-title">
          <span>Navigation</span>
        </div>

        {/* Navigation Items */}
        <div className="nav-items">
          <Link 
            to="/dashboard"
            className={`nav-item ${location.pathname === '/dashboard' ? 'active' : ''}`}
          >
            <span className="nav-icon">📊</span>
            <span className="nav-text">Dashboard</span>
          </Link>
          
          <Link 
            to="/rooms"
            className={`nav-item ${location.pathname.startsWith('/rooms') ? 'active' : ''}`}
          >
            <span className="nav-icon">🎯</span>
            <span className="nav-text">Game Rooms</span>
          </Link>
          
          {/* Phase 6 Features - Leaderboards & Rankings */}
          <Link 
            to="/leaderboard"
            className={`nav-item ${location.pathname.startsWith('/leaderboard') ? 'active' : ''}`}
          >
            <span className="nav-icon">🏆</span>
            <span className="nav-text">Leaderboard</span>
          </Link>
          
          {/* Phase 6 Features - Achievement System */}
          <Link 
            to="/achievements"
            className={`nav-item ${location.pathname.startsWith('/achievements') ? 'active' : ''}`}
          >
            <span className="nav-icon">🎯</span>
            <span className="nav-text">Achievements</span>
          </Link>
          
          {/* Phase 6 Features - Enhanced Statistics */}
          <Link 
            to="/statistics"
            className={`nav-item ${location.pathname.startsWith('/statistics') ? 'active' : ''}`}
          >
            <span className="nav-icon">📈</span>
            <span className="nav-text">Statistics</span>
          </Link>
          
          {/* Phase 6 Features - Game Replay System */}
          <Link 
            to="/replays"
            className={`nav-item ${location.pathname.startsWith('/replays') ? 'active' : ''}`}
          >
            <span className="nav-icon">🎬</span>
            <span className="nav-text">Replays</span>
          </Link>
          
          {/* Admin Panel - Only visible for admin users */}
          {isAdmin && (
            <Link 
              to="/admin"
              className={`nav-item ${location.pathname.startsWith('/admin') ? 'active' : ''}`}
            >
              <span className="nav-icon">👑</span>
              <span className="nav-text">Admin Panel</span>
            </Link>
          )}
          
          <Link 
            to="/profile"
            className={`nav-item ${location.pathname.startsWith('/profile') ? 'active' : ''}`}
          >
            <span className="nav-icon">👤</span>
            <span className="nav-text">Profile & Stats</span>
          </Link>
          
          <Link
            to="/settings"
            className={`nav-item ${location.pathname.startsWith('/settings') ? 'active' : ''}`}
          >
            <span className="nav-icon">⚙️</span>
            <span className="nav-text">Settings</span>
          </Link>
        </div>

        {/* User Section removed - now in PersistentHeader */}
      </div>

      {/* Main Content Area */}
      <main className="main-content">
        <Outlet />
      </main>
    </div>
    </>
  );
};

export default ResponsiveLayout;